import { api } from './api';
import { PaginatedResponse } from '@/types/api.types';

// MCC Code interfaces
export interface CHDCode {
  id: number;
  icd10_code: string;
  description_ro: string;
  description_en: string;
  eurocat_subgroup?: string | null;
  is_severe_chd: boolean;
  created_at?: string;
  usage_count?: number;
}

export interface CHDCodeData {
  icd10_code: string;
  description_ro: string;
  description_en: string;
  eurocat_subgroup?: string;
  is_severe_chd: boolean;
}

export interface CHDCodeFilters {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Cache for reference data
let chdCodesCache: CHDCode[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class CHDCodeService {
  
  // Admin endpoints
  async listCHDCodes(filters: CHDCodeFilters = {}): Promise<PaginatedResponse<CHDCode>> {
    const queryParams = new URLSearchParams();
    
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const url = `/api/admin/chd-codes${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await api.get<any>(url);
    
    // Map the backend response to PaginatedResponse structure
    return {
      data: response.data || [],
      total: response.pagination?.total || 0,
      page: response.pagination?.page || 1,
      limit: response.pagination?.limit || 20,
      pages: response.pagination?.pages || 1
    };
  }

  async getCHDCode(id: number): Promise<CHDCode> {
    const response = await api.get<any>(`/api/admin/chd-codes/${id}`);
    return response.data;
  }

  async createCHDCode(data: CHDCodeData): Promise<CHDCode> {
    // Validate data
    if (!data.icd10_code?.trim()) {
      throw new Error('Codul ICD-10 este obligatoriu');
    }
    if (!data.description_ro?.trim()) {
      throw new Error('Descrierea în română este obligatorie');
    }
    if (!data.description_en?.trim()) {
      throw new Error('Descrierea în engleză este obligatorie');
    }

    // Validate ICD-10 format
    const icd10Pattern = /^[A-Z]\d{2}(\.\d)?$/;
    if (!icd10Pattern.test(data.icd10_code.trim().toUpperCase())) {
      throw new Error('Formatul codului ICD-10 nu este valid (ex: Q21.0)');
    }

    const response = await api.post<any>('/api/admin/chd-codes', {
      icd10_code: data.icd10_code.trim().toUpperCase(),
      description_ro: data.description_ro.trim(),
      description_en: data.description_en.trim(),
      eurocat_subgroup: data.eurocat_subgroup?.trim() || null,
      is_severe_chd: Boolean(data.is_severe_chd)
    });

    // Clear cache after creation
    this.clearCache();
    
    return response.data;
  }

  async updateCHDCode(id: number, data: CHDCodeData): Promise<CHDCode> {
    // Validate data
    if (!data.icd10_code?.trim()) {
      throw new Error('Codul ICD-10 este obligatoriu');
    }
    if (!data.description_ro?.trim()) {
      throw new Error('Descrierea în română este obligatorie');
    }
    if (!data.description_en?.trim()) {
      throw new Error('Descrierea în engleză este obligatorie');
    }

    // Validate ICD-10 format
    const icd10Pattern = /^[A-Z]\d{2}(\.\d)?$/;
    if (!icd10Pattern.test(data.icd10_code.trim().toUpperCase())) {
      throw new Error('Formatul codului ICD-10 nu este valid (ex: Q21.0)');
    }

    const response = await api.put<any>(`/api/admin/chd-codes/${id}`, {
      icd10_code: data.icd10_code.trim().toUpperCase(),
      description_ro: data.description_ro.trim(),
      description_en: data.description_en.trim(),
      eurocat_subgroup: data.eurocat_subgroup?.trim() || null,
      is_severe_chd: Boolean(data.is_severe_chd)
    });

    // Clear cache after update
    this.clearCache();
    
    return response.data;
  }

  async deleteCHDCode(id: number): Promise<void> {
    await api.delete(`/api/admin/chd-codes/${id}`);
    // Clear cache after deletion
    this.clearCache();
  }

  // Reference endpoint with caching
  async getActiveCHDCodes(): Promise<CHDCode[]> {
    const now = Date.now();
    
    // Check cache
    if (chdCodesCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
      return chdCodesCache;
    }

    try {
      const response = await api.get<any>('/api/reference/chd-codes');
      
      // Update cache
      chdCodesCache = response.data || [];
      cacheTimestamp = now;
      
      return chdCodesCache || [];
    } catch (error) {
      // If API call fails, return empty array or cached data if available
      console.error('Failed to load MCC codes:', error);
      return chdCodesCache || [];
    }
  }

  // Force refresh cache
  async refreshCache(): Promise<CHDCode[]> {
    this.clearCache();
    return this.getActiveCHDCodes();
  }

  // Clear cache
  clearCache(): void {
    chdCodesCache = null;
    cacheTimestamp = null;
  }

  // Helper method to get MCC code label by ID
  async getCHDCodeLabel(id: number): Promise<string> {
    try {
      const codes = await this.getActiveCHDCodes();
      const code = codes.find(c => c.id === id);
      return code ? `${code.icd10_code} - ${code.description_ro}` : 'Necunoscut';
    } catch (error) {
      console.error('Failed to get MCC code label:', error);
      return 'Necunoscut';
    }
  }

  // Helper method to format MCC code display
  formatCHDCode(code: CHDCode): string {
    return `${code.icd10_code} - ${code.description_ro}`;
  }
}

export const chdCodeService = new CHDCodeService();